import { getConfig } from '@superbet-group/web.lib.config';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import {
    createSimpleGetter,
    createSimpleMutator,
} from '@superbet-group/web.lib.vue-utilities';
import { BonusType } from '@superbet-group/web.types';
import { ActionContext } from 'vuex';

import { IState } from '@app/store';
import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';
import {
    BetlerBonus,
    Bonus,
    ComtradeBonus,
} from '@core/models/bonus/Bonus';
import BonusEligibility from '@core/models/bonus/BonusEligibility';
import BonusProvider from '@core/models/bonus/BonusProvider';
import { BonusBalanceData } from '@core/models/user/BonusBalanceData';
import BonusCategory from '@models/bonus/BonusCategory';
import StoreUtil from '@store/StoreUtil';
import groupBy from '@utils/dataTransformation/groupBy';

import {
    adjustActiveBonuses,
    convertBonusDate,
    getActiveBonusEligibility,
    getActiveSportBonusFromBalance,
    getBetlerBonus,
    getBonusEligibilityAmount,
    getFirstSportBonus,
    getNoWelcomeBonusOption,
    getSportBonuses,
    isBetlerBonusEligibilityAvailable,
    orderActiveBonuses,
    sortByType,
    sortChronologically,
} from './helpers';
import { BonusState } from './types';
import { shouldWelcomeOfferModalReappear } from '../messages/messagesHelper';
import { BonusStatus } from '@core/models/bonus/BonusStatus';
import {
    getAvailableBonuses,
    getBonusBetEligibility,
    getPlayerBonuses,
} from '@core/services/bonus/BonusService';

const SET_ACTIVE_BONUSES = 'SET_ACTIVE_BONUSES';
const SET_WAGERABLE_BONUSES = 'SET_WAGERABLE_BONUSES';
const SET_UNWAGERABLE_BONUSES = 'SET_UNWAGERABLE_BONUSES';
const SET_CRITERIA_REQUIRED_BONUSES = 'SET_CRITERIA_REQUIRED_BONUSES';
const SET_HISTORIC_BONUSES = 'SET_HISTORIC_BONUSES';
const CLEAR_ACTIVE_SPORT_BONUS = 'CLEAR_ACTIVE_SPORT_BONUS';
const SET_ACTIVE_BONUS_ELIGIBILITY = 'SET_ACTIVE_BONUS_ELIGIBILITY';
const SET_WELCOME_BONUSES = 'SET_WELCOME_BONUSES';
const SET_ARE_WELCOME_BONUSES_FETCHED = 'SET_ARE_WELCOME_BONUSES_FETCHED';
const SET_WELCOME_OFFER_CAMPAIGN_ID = 'SET_WELCOME_BONUS_CAMPAIGN_ID';
export const ACTIVE_SPORT_BONUS_ID = 'ACTIVE_SPORT_BONUS_ID';
export const ACTIVE_BONUS_ELIGIBILITY = 'ACTIVE_BONUS_ELIGIBILITY';

export const BonusFlagNames = {
    isBonusAvailableFeatureFlag: 'betslip.bonus-available' as const,
};

type Keys = keyof typeof BonusFlagNames;
export type BonusFlagNames = typeof BonusFlagNames[Keys];

function defaultState(): BonusState {
    return {
        activeBonuses: [],
        wagerableBonuses: [],
        unwagerableBonuses: [],
        requiredCriteriaBonuses: [],
        historicBonuses: [],
        activeBonusEligibility: null,
        welcomeBonuses: [],
        areWelcomeBonusesFetched: false,
        welcomeOfferCampaignId: null,
    };
}

const bonusesStore = {
    namespaced: true,
    state: defaultState(),
    mutations: {
        [SET_ACTIVE_BONUSES]: createSimpleMutator<BonusState, 'activeBonuses'>('activeBonuses'),
        [SET_WAGERABLE_BONUSES]: createSimpleMutator<BonusState, 'wagerableBonuses'>('wagerableBonuses'),
        [SET_UNWAGERABLE_BONUSES]: createSimpleMutator<BonusState, 'unwagerableBonuses'>('unwagerableBonuses'),
        [SET_CRITERIA_REQUIRED_BONUSES]: createSimpleMutator<BonusState, 'requiredCriteriaBonuses'>('requiredCriteriaBonuses'),
        [SET_HISTORIC_BONUSES]: createSimpleMutator<BonusState, 'historicBonuses'>('historicBonuses'),
        [SET_ACTIVE_BONUS_ELIGIBILITY]: createSimpleMutator<BonusState, 'activeBonusEligibility'>('activeBonusEligibility'),
        [SET_WELCOME_BONUSES]: createSimpleMutator<BonusState, 'welcomeBonuses'>('welcomeBonuses'),
        [SET_ARE_WELCOME_BONUSES_FETCHED]: createSimpleMutator<BonusState, 'areWelcomeBonusesFetched'>('areWelcomeBonusesFetched'),
        [SET_WELCOME_OFFER_CAMPAIGN_ID]: createSimpleMutator<BonusState, 'welcomeOfferCampaignId'>('welcomeOfferCampaignId'),
        [CLEAR_ACTIVE_SPORT_BONUS]: (state: BonusState) => {
            clearActiveBonusData(state);
        },
    },
    getters: {
        areWelcomeBonusesFetched: createSimpleGetter<BonusState, keyof BonusState>('areWelcomeBonusesFetched'),
        activeBonusEligibility: createSimpleGetter<BonusState, 'activeBonusEligibility'>('activeBonusEligibility'),
    },
    actions: {
        async fetchBonusInformation(context: ActionContext<BonusState, IState>, shouldUpdateBalance = false) {
            const { commit } = context;

            const bonuses = await getPlayerBonuses(false);
            const sortedBonuses = sortByType(bonuses);
            const statusGroups = groupBy(sortedBonuses, 'category');
            const getBonuses = (category: BonusCategory) => statusGroups.get(category) || [];

            commit(SET_WAGERABLE_BONUSES, getBonuses(BonusCategory.AvailableToWager));
            commit(SET_UNWAGERABLE_BONUSES, getBonuses(BonusCategory.UnavailableToWager));
            commit(SET_CRITERIA_REQUIRED_BONUSES, getBonuses(BonusCategory.CriteriaRequired));

            const historicBonuses = sortChronologically(getBonuses(BonusCategory.History));
            commit(SET_HISTORIC_BONUSES, historicBonuses);

            let activeBonuses: Bonus[] = [];
            if (bonuses) {
                if (getConfig().countrySpecific.user.bonus.bonusProvider === BonusProvider.Comtrade) {
                    const unadjustedActiveBonuses = bonuses.filter((bonus) => bonus.category && bonus.category !== BonusCategory.History && bonus.status !== BonusStatus.Waiting);
                    activeBonuses = await adjustActiveBonuses(context, unadjustedActiveBonuses as ComtradeBonus[], shouldUpdateBalance);
                } else {
                    activeBonuses = orderActiveBonuses(getBonuses(BonusCategory.Active) as BetlerBonus[]);
                }
            }
            commit(SET_ACTIVE_BONUSES, activeBonuses);
        },

        async _fetchBetBonusEligibility(context: ActionContext<BonusState, IState>) {
            const user = context.rootGetters['data/authentication/user'];

            if (user) {
                const isFreeBetBonusEnabled = await featureFlagService.getFeatureValue(BonusFlagNames.isBonusAvailableFeatureFlag, false);
                if (isFreeBetBonusEnabled) {
                    const bonusEligibilities = await getBonusBetEligibility();
                    const activeBonusEligibility = getActiveBonusEligibility(bonusEligibilities);

                    if (activeBonusEligibility) {
                        clientStorageWithExpiry.set(ACTIVE_BONUS_ELIGIBILITY, activeBonusEligibility);
                        context.commit(SET_ACTIVE_BONUS_ELIGIBILITY, activeBonusEligibility);
                        context.dispatch('ui/sportOffer/betSlip/calculatePayout', null, { root: true });
                        context.dispatch('ui/sportOffer/betSlip/checkBetSlipValidation', null, { root: true });
                    }
                }
            }
        },

        /**
         * Method checks and saves currently active sport bonus eligibility and activeSportBonusId to the store and to local storage.
         * This active sport bonus eligibility is needed for showing betSlip breakdown (how much money is from bonus and how much from user balance).
         * In order to save load and not fetch bonus every time, method compares activeSportBonusId that we get from getPlayerBalance with the one from local storage.
         *
         * NOTICE: Betler is currently working on getBetEligibility API endpoint. Until then, we handle Betler bonus eligibility differently.
         */
        async checkActiveBonuses(context: ActionContext<BonusState, IState>) {
            if (getConfig().countrySpecific.user.bonus.bonusProvider === BonusProvider.Betler) {
                context.dispatch('_handleBetlerBonus');
                return;
            }

            const storedSportBonusId = clientStorageWithExpiry.get(ACTIVE_SPORT_BONUS_ID);
            const activeSportBonusId = getActiveSportBonusFromBalance(context.rootGetters);

            if (activeSportBonusId) {
                if (activeSportBonusId === storedSportBonusId && clientStorageWithExpiry.get(ACTIVE_BONUS_ELIGIBILITY)) {
                    context.dispatch('_updateBonusAmount');
                } else {
                    clientStorageWithExpiry.set(ACTIVE_SPORT_BONUS_ID, activeSportBonusId);
                    await context.dispatch('_fetchBetBonusEligibility');
                }
            } else {
                clearActiveBonusData(context.state);
            }
            context.dispatch('ui/sportOffer/betSlip/checkBetSlipValidation', null, { root: true });
        },

        _updateBonusAmount(context: ActionContext<BonusState, IState>) {
            const activeBonusBalances: BonusBalanceData = context.rootGetters['data/authentication/userBonusBalanceData'];
            const storedBonus: BonusEligibility | null = clientStorageWithExpiry.get(ACTIVE_BONUS_ELIGIBILITY);
            if (storedBonus) {
                const newBonusAmount = getBonusEligibilityAmount(storedBonus, activeBonusBalances);
                if (storedBonus.balance !== newBonusAmount || context.state.activeBonusEligibility === null) {
                    storedBonus.balance = newBonusAmount;
                    storedBonus.expirationDate = convertBonusDate(storedBonus.expirationDate);
                    clientStorageWithExpiry.set(ACTIVE_BONUS_ELIGIBILITY, storedBonus);
                    clientStorageWithExpiry.set(ACTIVE_SPORT_BONUS_ID, storedBonus.bonusId);
                    context.commit(SET_ACTIVE_BONUS_ELIGIBILITY, storedBonus);
                }
            }
        },

        /**
         * Bonus eligibility is available in bonus object fetched from getPlayerBonuses endpoint
         */
        async _handleBetlerBonus(context: ActionContext<BonusState, IState>) {
            const activeBonusBalances: BonusBalanceData = context.rootGetters['data/authentication/userBonusBalanceData'];
            if (activeBonusBalances
                && (activeBonusBalances[BonusType.Freebet]?.amount || activeBonusBalances[BonusType.Sport]?.amount)) {
                await context.dispatch('fetchBonusInformation');
                const activeSportBonuses = getSportBonuses(context.state.activeBonuses as BetlerBonus[]);

                if (isBetlerBonusEligibilityAvailable(activeSportBonuses)) {
                    const firstSportBonus = getFirstSportBonus(activeSportBonuses);
                    context.commit(SET_ACTIVE_BONUS_ELIGIBILITY, getBetlerBonus(firstSportBonus, activeBonusBalances));
                } else {
                    clearActiveBonusData(context.state);
                }
            } else {
                clearActiveBonusData(context.state);
            }
            context.dispatch('ui/sportOffer/betSlip/calculatePayout', null, { root: true });
            context.dispatch('ui/sportOffer/betSlip/checkBetSlipValidation', null, { root: true });
        },

        clearActiveBonus: StoreUtil.createSimpleMutatorAction(CLEAR_ACTIVE_SPORT_BONUS),

        async refreshActiveBonuses(context: ActionContext<BonusState, IState>) {
            const newBonuses = await adjustActiveBonuses(context, context.state.activeBonuses as ComtradeBonus[], false);
            context.commit(SET_ACTIVE_BONUSES, newBonuses);
        },

        async fetchWelcomeBonuses(context: ActionContext<BonusState, IState>) {
            const welcomeBonusResponse = await getAvailableBonuses();
            const user = context.rootGetters['data/authentication/user'];
            if (
                welcomeBonusResponse.availableBonuses?.length > 0
                && shouldWelcomeOfferModalReappear(user.userId, welcomeBonusResponse.welcomeOfferCampaignId)
            ) {
                context.commit(SET_ARE_WELCOME_BONUSES_FETCHED, true);
                welcomeBonusResponse.availableBonuses.push(getNoWelcomeBonusOption());
                context.commit(SET_WELCOME_BONUSES, welcomeBonusResponse.availableBonuses);
            } else {
                context.commit(SET_ARE_WELCOME_BONUSES_FETCHED, false);
            }

            if (welcomeBonusResponse.welcomeOfferCampaignId) {
                context.commit(SET_WELCOME_OFFER_CAMPAIGN_ID, welcomeBonusResponse.welcomeOfferCampaignId);
            }
        },
    },
};

export type BonusStore = typeof bonusesStore;

export default bonusesStore;

export function clearActiveBonusData(state: BonusState) {
    state.activeBonusEligibility = null;
    clientStorageWithExpiry.remove(ACTIVE_SPORT_BONUS_ID);
    clientStorageWithExpiry.remove(ACTIVE_BONUS_ELIGIBILITY);
}
